<template>
    <div id="payment-form">
        <div class="uk-padding uk-text-center">
            <h2>
                New Payment
            </h2>

            <div class="uk-container-xsmall uk-margin-auto-left uk-margin-auto-right">
                <ul class="steps">
                    <li
                        v-for="stepNo in 4"
                        :key="stepNo"
                        class="step"
                    >
                        <div
                            :class="{active: stepNo == step}"
                        >
                            {{ stepNo }}
                        </div>
                        {{ steps[stepNo - 1] }}
                    </li>
                </ul>
            </div>
        </div>

        <div class="panel uk-container-xsmall uk-padding-large uk-margin-auto-left uk-margin-auto-right">
            <system-message
                :success="success"
                :message="message"
                @close="clearSystemMessage"
            />
            <div id="payment-details">
                <form-wrapper
                    class="uk-form-stacked"
                    :validator="$v.payment"
                    :messages="messagesOverride"
                >
                    <div v-if="step === 1">
                        <div
                            uk-grid
                            class="uk-grid-small uk-margin-bottom@l"
                        >
                            <div class="uk-width-1-1@s uk-width-1-6@l uk-flex uk-flex-middle">
                                <label class="uk-form-label">Job Number</label>
                            </div>
                            <text-field
                                v-model="payment.jobNumber"
                                placeholder=""
                                name="jobNumber"
                                :v="$v.payment.jobNumber"
                                class="uk-width-1-2@s uk-width-3-6@l"
                                :show-error-on-blur-only="true"
                                :hide-label="true"
                                @input="requireJobDetailsRetrieval"
                                @onEnter="onJobNumberEnter"
                            />

                            <div
                                v-if="shouldJobDetailBeRetrieved"
                                class="uk-width-1-2@s uk-width-1-6@l"
                            >
                                <button
                                    type="button"
                                    class="uk-button uk-button-primary"
                                    :disabled="$v.payment.jobNumber.$invalid"
                                    @click="retrieveJobDetails"
                                >
                                    Retrieve saved job details
                                </button>
                            </div>
                        </div>

                        <div
                            v-if="isJobDetailRetrieved"
                            class="uk-padding uk-padding-remove-left uk-padding-remove-right uk-padding-remove-bottom"
                        >
                            <div v-if="jobDetails">
                                <h4>
                                    JOB DETAILS FOUND IN FRAMEWORK
                                </h4>
                                <div>
                                    <table>
                                        <tr
                                            v-for="(value, fieldName) in jobDetails"
                                            :key="fieldName"
                                        >
                                            <td>{{ fieldName }}</td>
                                            <td>:</td>
                                            <td>{{ value }}</td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                            <br/>
                            <div v-if="isUserDetailRetrieved">
                                <h4>
                                    SAVED USER DETAILS
                                </h4>
                                <div v-if="primaryUser">
                                    <h5>Primary Contact</h5>
                                    <table>
                                        <tr>
                                            <td>Customer</td>
                                            <td>:</td>
                                            <td>{{ getUserName(primaryUser.user) }}</td>
                                        </tr>
                                        <tr>
                                            <td>User Address</td>
                                            <td>:</td>
                                            <td>{{ getUserAddress(primaryUser.address) }}</td>
                                        </tr>
                                    </table>
                                </div>
                                <div v-else>
                                    <h5>( No Saved Primary Contact )</h5>
                                </div>
                                <br/>
                                <div
                                    v-if="alternateContacts.length > 0"
                                    class="uk-margin-large-top"
                                >
                                    <h5>Alternate Contact{{alternateContacts.length > 1 ? 's' : ''}}</h5>
                                    <div
                                        v-for="contact in alternateContacts"
                                        :key="contact.id"
                                    >
                                        <div class="uk-margin-medium-bottom">
                                            <table>
                                                <tr>
                                                    <td>Customer</td>
                                                    <td>:</td>
                                                    <td>{{ getUserName(contact.user) }}</td>
                                                </tr>
                                                <tr>
                                                    <td>User Address</td>
                                                    <td>:</td>
                                                    <td>{{ getUserAddress(contact.address) }}</td>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                Job exists but no saved user found. You may add a user in Customer step.
                            </div>
                        </div>
                    </div>

                    <div v-if="step === 2">
                        <div
                            v-if="!hasOnlyOneReceiptTypeOption"
                            class="uk-grid uk-grid-small"
                        >
                            <div class="uk-width-1-1@s uk-width-1-2@l">
                                <select-field
                                    v-model="payment.receiptTypeID"
                                    placeholder="Receipt Type"
                                    name="receiptTypeID"
                                    :options="receiptTypes"
                                    :v="$v.payment.receiptTypeID"
                                    label="Receipt Type"
                                    :show-error-on-blur-only="true"
                                    @onInputChange="resetPaymentMethod"
                                />
                            </div>
                        </div>

                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-1-1@s uk-width-1-2@l">
                                <select-field
                                    v-model="payment.paymentType"
                                    placeholder="Payment Type"
                                    name="paymentType"
                                    :options="sortedPaymentTypes"
                                    :v="$v.payment.paymentType"
                                    label="Payment Type"
                                    :show-error-on-blur-only="true"
                                    @onInputChange="resetPaymentMethod"
                                />
                            </div>
                            <div class="uk-width-1-1@s uk-width-1-2@l">
                                <formatted-numeric-field
                                    v-model="payment.amount"
                                    placeholder="0.00"
                                    name="amount"
                                    label="Amount"
                                    :v="$v.payment.amount"
                                    :options="amountFieldOptions"
                                    :show-error-on-blur-only="true"
                                    @onInputBlur="computeAmountAndSurcharge"
                                />
                            </div>
                        </div>

                        <div
                            uk-grid
                            class="uk-grid-small"
                        >
                            <text-field
                                v-model="payment.notes"
                                :placeholder="descriptionPlaceholder"
                                name="notes"
                                label="Description"
                                :v="$v.payment.notes"
                                class="uk-width-1-1"
                                :show-error-on-blur-only="true"
                            />
                        </div>

                        <label class="uk-form-label">Payment Method</label>

                        <div class="uk-flex-inline uk-margin-bottom">
                            <div
                                v-for="(label, value) in paymentMethodsToShow"
                                :key="value"
                                class="uk-margin-right payment-method uk-text-center"
                                :class="{ active: value === payment.paymentMethod }"
                                @click="setPaymentMethod(value)"
                            >
                                <payment-method-icon
                                    :value="value"
                                    :active="value === payment.paymentMethod"
                                />
                                <span>{{ getPaymentMethodLabel(label, value) }}</span>
                            </div>
                        </div>

                        <!-- SIC-262: Simonds will absorb surcharge
                        <div
                            v-if="payment.paymentState && payment.paymentType && payment.paymentMethod"
                            uk-grid
                            class="uk-grid-small"
                        >
                            <div
                                v-if="isCreditCardPayment"
                                class="uk-margin-small-top"
                            >
                                <small class="uk-text-muted">
                                    {{ surchargeNote }}
                                </small>
                            </div>
                        </div>
                        -->

                        <div v-if="shouldShowReceiptWarning">
                            <p class="uk-text-small">
                                The customer will not be emailed a receipt until this payment has been reconciled by the Accounts team.
                            </p>
                        </div>
                        <div v-if="isCreditCardPayment">
                            <checkbox-field
                                id="emailBpointPaymentLink"
                                v-model="emailBpointPaymentLink"
                                label="Email Payment Link"
                                name="emailCardPaymentLink"
                            />
                        </div>
                        <credit-card-payment-form
                            v-if="isCreditCardPayment && !emailBpointPaymentLink"
                            :cc="cc"
                            :enable-validation="true"
                            @change="onCreditCardPaymentFormChange"
                        />
                        <cheque-payment-form
                            v-if="isChequePayment"
                            :cheque="cheque"
                            :enable-validation="true"
                            @change="onChequePaymentFormChange"
                        />
                    </div>
                </form-wrapper>

                <div v-if="step ===3">
                    <h4>Customer details</h4>
                    <p v-if="isUserAlreadyExisting">
                        The Job Number entered has existing Customer details saved. <br>
                        Select the existing Customer(s) below or create an Additional Customer. <br>
                        <b>If the name or address were parsed incorrectly, please update them here.</b>
                    </p>
                    <p v-else>
                        There are no Customer details saved to this Job Number. <br>
                        Please add New Customer details below.
                    </p>

                    <div class="uk-grid uk-padding uk-padding-remove-left uk-padding-remove-right">
                        <div
                            v-for="buildUser in buildUsers"
                            :key="buildUser.id"
                            class="uk-width-1-1@s uk-width-1-2@l uk-margin-bottom"
                            @click="retrieveUserDetails(buildUser.user.email, buildUser.isPrimary)"
                        >
                            <button
                                type="button"
                                class="uk-button uk-button-link customer-link"
                                :class="{ active: buildUser.user.email === payer.user.email }"
                                @click="retrieveUserDetails(buildUser.user.email, buildUser.isPrimary)"
                            >
                                <div class="uk-inline user-icon">
                                    <user-icon
                                        class="uk-margin-small-right uk-margin-small-left"
                                        :active="buildUser.user.email === payer.user.email"
                                    />
                                </div>

                                &nbsp; {{ buildUser.user.fullName }} {{ buildUser.isPrimary ? "(Primary)" : "" }}
                            </button>
                        </div>

                        <div
                            class="uk-width-1-1@s uk-width-1-2@l"
                        >
                            <button
                                type="button"
                                class="uk-button uk-button-link add-customer-link"
                                :class="{ active: !isUserDetailRetrieved && isUserFormVisible}"
                                @click="clearUserDetails"
                            >
                                <div
                                    class="uk-inline uk-margin-small-right add-customer-icon"
                                    :class="{ active: !isUserDetailRetrieved && isUserFormVisible}"
                                >
                                    <span
                                        class="uk-margin-small-right uk-margin-small-left "
                                        uk-icon="plus"
                                    />
                                </div>

                                Add new customer
                            </button>
                        </div>
                    </div>

                    <div v-if="showUserForm">
                        <header
                            v-if="!isUserDetailRetrieved"
                            uk-grid
                            class="user-details-header uk-margin-bottom"
                        >
                            <h4> New Customer Details </h4>
                        </header>
                        <user-details-form
                            :key="userDetailsFormKey"
                            mode="create"
                            :enable-validation="true"
                            :should-job-detail-be-retrieved="shouldJobDetailBeRetrieved"
                            :user-details="payer"
                            :is-user-detail-retrieved="isUserDetailRetrieved"
                            :states="states"
                            :titles="titles"
                            :is-credit-card-payment="isCreditCardPayment"
                            :show-is-primary="showIsPrimary"
                            @change="onUserFormChange"
                        />
                    </div>
                </div>

                <div v-if="step === 4">
                    <payment-summary
                        :payment-details="paymentDetailsToConfirm"
                    />
                </div>
            </div>

            <div
                v-if="!shouldJobDetailBeRetrieved"
                class="api-node uk-margin-large-top"
                uk-grid
            >
                <div class="uk-width-1-1 uk-flex uk-flex-between">
                    <button
                        type="button"
                        class="uk-button uk-button-primary uk-margin-right"
                        :disabled="step === 1"
                        @click="goToPreviousStep"
                    >
                        Back
                    </button>
                    <div class="uk-margin-auto-left">
                        <button
                            v-if="showNextButton"
                            :disabled="!isNextStepValid"
                            type="button"
                            class="uk-button uk-button-primary uk-margin-right"
                            @click="goToNextStep"
                        >
                            Next
                        </button>
                        <button
                            v-else
                            type="button"
                            class="uk-button uk-button-primary uk-margin-right"
                            @click="processPayment"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="uk-text-center">
            <button
                type="button"
                class="uk-button uk-button-link"
                @click="goToIndex()"
            >
                Cancel and return to dashboard
            </button>
        </div>
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
import { required, minLength } from "vuelidate/lib/validators";
import {
    getAuthKeyRequest,
    getJobDetailsRequest,
    getPaymentFormDropdownItemsRequest,
    processCashRequest,
    processChequeRequest,
    processEftRequest,
    verifyPaymentRequest,
    deletePaymentRequest,
    computeAmountAndSurchargeRequest,
    getUserDetailsByEmailRequest,
    sendBpointPaymentLinkRequest
} from '../../helpers/api'
import { updateLoader } from '@strangeanimals/common-vue-components/src/helpers/api'
import { cloneObject, copyData, sortByKey } from '@strangeanimals/common-vue-components/src/helpers/helpers';
import router from '../../helpers/router'
import { DEFAULT_TENDER_FEE_AMOUNT, PaymentMethods, PaymentTypes, ReceiptTypes } from '../../helpers/constants'

const TextField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/TextField");
const SelectField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/SelectField");
const SystemMessage  = () => import( "@strangeanimals/common-vue-components/src/components/page/SystemMessage");
const FormattedNumericField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/FormattedNumericField");
const CheckboxField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/CheckboxField");

const ChequePaymentForm  = () => import( "../forms/ChequePaymentForm");
const CreditCardPaymentForm  = () => import( "../forms/CreditCardPaymentForm");
const UserDetailsForm  = () => import( "../forms/UserDetailsForm");
const PaymentSummary = () => import("../PaymentSummary");
const PaymentMethodIcon = () => import("../icons/PaymentMethodIcon");
const UserIcon = () => import("../icons/UserIcon");

export default {
    name: 'CreatePage',
    components: { PaymentSummary, FormattedNumericField, UserDetailsForm, CreditCardPaymentForm, ChequePaymentForm, TextField, SelectField, SystemMessage, UserIcon, PaymentMethodIcon, CheckboxField },
    data: function () {
        return {
            id: null,
            payment: {
                jobNumber: '',
                paymentState: '',
                paymentType: '',
                paymentMethod: '',
                receiptTypeID: '',
                amount: '',
                notes: '',
                transaction: {}
            },
            paymentUser: {},
            cc: {
                cardNumber: '',
                expiryMonth: '',
                expiryYear: '',
                cvn: '',
                cardholderName: '',
            },
            cheque: {
                bank: '',
                branch: '',
                drawer: '',
            },
            payer: {
                user: {
                    title: '',
                    firstName: '',
                    surname: '',
                    mobileNumber: '',
                    email: '',
                },
                address: {
                    address1: '',
                    address2: '',
                    suburb: '',
                    state: '',
                    postcode: '',
                },
                isPrimary: false
            },
            titles: {},
            paymentTypes: {},
            paymentStates: {},
            paymentMethods: {},
            surchargeForStates: {},
            receiptTypes: {},
            states: {
                VIC: 'VIC',
                WA: 'WA',
                NSW: 'NSW',
                TAS: 'TAS',
                NT: 'NT',
                QLD: 'QLD',
                SA: 'SA'
            },
            success: true,
            message: [],
            bpointErrors: [],
            isJobDetailRetrieved: false,
            isUserDetailRetrieved: false,
            isChequePaymentFormValid: false,
            isCreditCardPaymentFormValid: false,
            isUserFormValid: false,
            messagesOverride: {
                minLength: "Field {attribute} should be 5 characters.",
                maxLength: "Field {attribute} should be 5 characters."
            },
            amountWithBpointSurcharge: null,
            surchargeAmount: null,
            amountWithoutSurcharge: null,
            amountFieldOptions: {
                prefix: '$ ',
                numeral: true,
                numeralPositiveOnly: true,
                noImmediatePrefix: true,
                rawValueTrimPrefix: true,
                numeralIntegerScale: 9,
                numeralDecimalScale: 2
            },
            buildUsers: [],
            jobDetails: null,
            isUserFormVisible: false,
            userDetailsFormKey: 0,
            step: 1,
            steps: ['Job', 'Payment', 'Customer', 'Confirm'],
            emailBpointPaymentLink: false
        };
    },
    computed: {
        ...mapGetters({
            payments: 'payments',
            loggedInUser: 'user',
            isUserAdmin: 'isUserAdmin',
            defaultRequestParams: 'defaultRequestParams',
            isLoading: 'isLoading'
        }),
        isFormValid: function () {
            return this.isJobDetailRetrieved
                && !this.$v.payment.$invalid
                && this.isUserFormVisible
                && this.isUserFormValid
                && this.isCreditCardSectionValid
                && this.isChequeSectionValid;
        },
        isNextStepValid: function () {
            switch (this.step) {
            case 1:
            case 4:
                return true;
            case 2:
                return !this.$v.payment.$invalid && this.isCreditCardSectionValid && this.isChequeSectionValid;
            case 3:
                return this.isUserFormVisible && this.isUserFormValid;
            default:
                return false;
            }
        },
        isCreditCardSectionValid: function () {
            return !this.isCreditCardPayment || this.isCreditCardPaymentFormValid || this.emailBpointPaymentLink;
        },
        isChequeSectionValid: function () {
            return !this.isChequePayment || this.isChequePaymentFormValid;
        },
        sortedPaymentTypes: function () {
            return sortByKey(this.paymentTypes)
        },
        sortedPaymentStates: function () {
            return sortByKey(this.paymentStates)
        },
        isCashPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CASH;
        },
        isCreditCardPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CREDIT_CARD;
        },
        isEftPayment: function () {
            return this.payment.paymentMethod === PaymentMethods.EFT;
        },
        isChequePayment: function () {
            return this.payment.paymentMethod === PaymentMethods.CHEQUE;
        },
        isPaymentTypeOther: function () {
            return this.payment.paymentType === PaymentTypes.OTHER;
        },
        isPreliminaryPaymentType: function () {
            return ((this.payment.paymentType === PaymentTypes.INITIAL_PRELIMINARY_FEE) || (this.payment.paymentType === PaymentTypes.FURTHER_PRELIMINARY_FEE));
        },
        isTenderFeePaymentType: function () {
            return (this.payment.paymentType === PaymentTypes.TENDER_FEE);
        },
        isMasterpiecePayment: function () {
            return this.receiptTypes[this.payment.receiptTypeID] === ReceiptTypes.MASTERPIECE;
        },
        payload: function () {
            return {
                ...this.defaultRequestParams,
                ...this.objectDetails,
                ...this.paymentDetails,
                payload: this.userDetails
            }
        },
        paymentDetailsToConfirm: function () {
            return {
                ...this.paymentDetails,
                jobNumber: this.payment.jobNumber,
                paymentType: this.payment.paymentType,
                paymentMethod: this.payment.paymentMethod,
                paymentMethodDisplayTitle: this.paymentMethods[this.payment.paymentMethod],
                paymentState: this.paymentStates[this.objectDetails.paymentState],
                cc: this.cc,
                cheque: this.chequeDetails,
                isCCPayment: this.isCreditCardPayment,
                isChequePayment: this.isChequePayment,
                payer: this.userDetails,
                surchargeNote: this.surchargeNote,
                totalAmount: this.amountWithBpointSurcharge,
                surchargeAmount: this.surchargeAmount,
                amountWithoutSurcharge: this.amountWithoutSurcharge,
                isUserDetailRetrieved: this.isUserDetailRetrieved,
                emailBpointPaymentLink: this.emailBpointPaymentLink
            }
        },
        paymentDetails: function () {
            return {
                amount: this.payment.amount,
                notes: this.payment.notes,
                receiptTypeID: this.payment.receiptTypeID
            };
        },
        objectDetails: function () {
            return {
                objectType: 'build',
                idName: 'job_number',
                idValue: this.payment.jobNumber,
                paymentType: this.payment.paymentType,
                paymentState: this.payment.paymentState
            }
        },
        chequeDetails: function () {
            return {
                bank: this.cheque.bank,
                branch: this.cheque.branch,
                drawer: this.cheque.drawer,
            }
        },
        userDetails: function () {
            return {
                user: {
                    title: this.payer.user.title,
                    first_name: this.payer.user.firstName,
                    surname: this.payer.user.surname,
                    mobile_number: this.payer.user.mobileNumber,
                    email: this.payer.user.email,
                },
                address: {
                    address_1: this.payer.address.address1,
                    address_2: this.payer.address.address2,
                    suburb: this.payer.address.suburb,
                    state: this.payer.address.state,
                    postcode: this.payer.address.postcode
                },
                isPrimary: this.payer.isPrimary
            };
        },
        shouldJobDetailBeRetrieved() {
            return !this.isJobDetailRetrieved;
        },
        showUserForm() {
            return this.isJobDetailRetrieved && this.isUserFormVisible;
        },
        canEditPaymentStatus() {
            return (this.isUserAdmin && !this.isCreditCardPayment);
        },
        descriptionPlaceholder() {
            let placeholder = "";
            if (this.isPaymentTypeOther) {
                placeholder = "Please enter the details of your payment here.";
            } else if (this.isPreliminaryPaymentType) {
                // ZOSIM-104: Masterpiece doesn't use salesforce
                if (this.isMasterpiecePayment) {
                    placeholder = "Enter notes here.";
                } else {
                    placeholder = "Enter the Salesforce deposit number here.";
                }
            }
            return placeholder;
        },
        surchargePercentage() {
            return parseFloat(this.surchargeForStates[this.payment.paymentState]);
        },
        surchargeNote() {
            return "There is a " + this.surchargePercentage.toString() + "% fee for credit card transactions.";
        },
        paymentMethodsToShow() {
            // ZOSIM-18: when payment is initial or further preliminary fee, disable credit-card
            // ZOSIM-21: disable eft unless payment is initial or further preliminary fee
            // But we only need to do this for new payments. Payment methods for old payments on edit mode cannot be changed.
            // ZOSIM-104: can you please make “Credit or Debit Card” the only form of payment allowed for Masterpiece users
            let paymentMethods = cloneObject(this.paymentMethods);
            if ((this.isMasterpiecePayment) || (this.isTenderFeePaymentType)) {
                delete paymentMethods[PaymentMethods.CASH];
                delete paymentMethods[PaymentMethods.EFT];
                delete paymentMethods[PaymentMethods.CHEQUE];
            } else {
                if (this.isPreliminaryPaymentType) {
                    delete paymentMethods[PaymentMethods.CREDIT_CARD];
                }

                if (!this.isPreliminaryPaymentType) {
                    delete paymentMethods[PaymentMethods.EFT];
                }
            }
            return paymentMethods;
        },
        shouldShowReceiptWarning() {
            return this.isCashPayment || this.isEftPayment || this. isChequePayment;
        },
        isUserAlreadyExisting() {
            return this.buildUsers.length > 0;
        },
        showIsPrimary() {
            const payerEmail = this.payer.user.email;
            const isPayerEmailAlreadyIncludedInBuildUsers = this.buildUsers.find((user) => (user.user.email ===  payerEmail)) !== undefined;
            return (this.buildUsers.length > 1) || ((this.buildUsers.length === 1) && !isPayerEmailAlreadyIncludedInBuildUsers);
        },
        hasOnlyOneReceiptTypeOption() {
            return Object.keys(this.receiptTypes).length === 1;
        },
        billerCodeSelector() {
            let result = 'payments-app';
            // ZOSIM-106: Initial or Further preliminary payments with amount <= 3000 will have no surcharge.
            if (this.isPreliminaryPaymentType && this.payment.amount.length > 0 && this.payment.amount <= 3000) {
                result = 'payments-app-no-fee';
            }
            return result;
        },
        bpointScriptUrl() {
            return "https://www.bpoint.com.au/webapi/CBA/api.js?v=2";
        },
        isBpointScriptLoaded() {
            let scripts = document.getElementsByTagName('script');
            for (let i = 0; i < scripts.length; i++) {
                if (scripts[i].src === this.bpointScriptUrl) return true;
            }
            return false;
        },
        primaryUser() {
            return this.buildUsers.find(user => (user.isPrimary === 1))
        },
        alternateContacts() {
            return this.buildUsers.filter(user => (user.isPrimary === 0));
        },
        showNextButton() {
            return this.step >= 1 && this.step <= 3;
        }
    },
    watch: {
        bpointErrors: function () {
            var errors = [];
            for ( var key in this.bpointErrors ) {
                errors.push(this.bpointErrors[key].Message);
            }
            this.setSystemMessage(errors, false);
        },
        payment: function () {
            if (this.isChequePayment) {
                this.cheque = this.payment.transaction
            }
        },
        paymentUser: {
            handler() {
                copyData(this.paymentUser.user, this.payer.user);
                copyData(this.paymentUser.address, this.payer.address);
            },
            deep: true
        },
        emailBpointPaymentLink: function() {
            if (this.emailBpointPaymentLink) {
                this.cc.cardNumber = '';
                this.cc.expiryMonth = '';
                this.cc.expiryYear = '';
                this.cc.cvn = '';
                this.cc.cardholderName = '';
            }
        }
    },
    mounted() {
        if (!this.isBpointScriptLoaded) {
            let bpointScript = document.createElement('script');
            bpointScript.setAttribute('src', this.bpointScriptUrl);
            bpointScript.setAttribute('type', "text/javascript");
            document.body.appendChild(bpointScript);
        }

        this.getPaymentFormDropdownItems();
    },
    validations() {
        return {
            payment: {
                jobNumber: { required, minLength: minLength(5) },
                paymentState: { required },
                paymentType: { required },
                paymentMethod: { required },
                receiptTypeID: { required },
                amount: { required },
                notes: this.isPaymentTypeOther ? { required } : {}
            }
        };
    },
    methods: {
        processPayment() {
            if (this.isCreditCardPayment) {
                if (this.emailBpointPaymentLink) {
                    this.sendBpointPaymentLink();
                } else {
                    this.getAuthKey();
                }
            }
            else if (this.isChequePayment) {
                this.processChequePayment();
            }
            else if (this.isEftPayment) {
                this.processEftPayment();
            }
            else {
                this.processCashPayment();
            }
        },
        processCashPayment() {
            var self = this;
            const data = {
                ...this.payload,
                receivedBy: this.loggedInUser.id
            };
            processCashRequest(data).then((response) => {
                self.redirectAfterPayment(response.body.approved);
            });
        },
        processChequePayment() {
            var self = this;
            const data = {
                ...this.payload,
                ...this.chequeDetails,
                receivedBy: this.loggedInUser.id
            };
            processChequeRequest(data).then((response) => {
                self.redirectAfterPayment(response.body.approved);
            });
        },
        processEftPayment() {
            var self = this;
            const data = {
                ...this.payload,
                receivedBy: this.loggedInUser.id
            };
            processEftRequest(data).then((response) => {
                self.redirectAfterPayment(response.body.approved);
            });
        },
        getAuthKey() {
            const self = this;
            const url = new URL(window.location.href);
            url.pathname = '';

            getAuthKeyRequest({
                ...this.payload,
                redirectURL: url.href,
                billerCodeSelector: self.billerCodeSelector
            }).then((response) => {
                self.processCCPayment(response.body.authKey);
            });
        },
        processCCPayment(authKey) {
            var self = this;
            var processPaymentCallback = function (result) {
                if (result.AjaxResponseType === 0) {
                    // AJAX call was successful
                    if (result.ApiResponseCode === 0) {
                        verifyPaymentRequest({
                            resultKey: result.ResultKey,
                            ...self.objectDetails,
                            ...self.paymentDetails,
                            payload: self.userDetails
                        }).then(response => {
                            self.redirectAfterPayment(response.body.approved);
                        });
                    }
                    else if (result.ApiResponseCode === 300) {
                        if (result.RedirectionUrl != null && result.RedirectionUrl.length > 0) {
                            window.location.href = result.RedirectionUrl;
                        }
                        else {
                            self.bpointErrors = result.Errors;
                        }
                    }
                    else {
                        self.bpointErrors = result.Errors;
                    }
                }
                else if (result.AjaxResponseType === 1) {
                    // Error with AJAX call
                    self.bpointErrors = result.Errors;
                }
                else if (result.AjaxResponseType === 2) {
                    // AJAX call timed out
                    self.bpointErrors = result.Errors;
                }
                updateLoader(-1, true);
            };

            updateLoader(1, true);
            // eslint-disable-next-line no-undef
            CBA.ProcessPayment({
                AuthKey: authKey,
                CardNumber: this.cc.cardNumber,
                Cvn: this.cc.cvn,
                ExpiryMonth: this.cc.expiryMonth,
                ExpiryYear: this.cc.expiryYear,
                CardHolderName: this.cc.cardholderName,
                CallbackFunction: processPaymentCallback,
                Type: "CREDITCARD"
            });
        },
        sendBpointPaymentLink() {
            var self = this;
            const data = {
                ...this.payload,
                receivedBy: this.loggedInUser.id
            };
            sendBpointPaymentLinkRequest(data).then((response) => {
                if (response.body.approved) {
                    self.setSystemMessage(["Payment link is sent."], true);
                    self.goToIndex();
                }
                else {
                    this.setSystemMessage(["An error occured. Please try again."], false);
                }
            });
        },
        redirectAfterPayment(approved) {
            if (approved) {
                this.setSystemMessage(["Payment is approved."], true);
                this.goToIndex();
            }
            else {
                this.setSystemMessage(["Payment is declined."], false);
            }
        },
        setSystemMessage(message, isSuccess) {
            this.message = message;
            this.success = isSuccess;
        },
        requireJobDetailsRetrieval() {
            this.isJobDetailRetrieved = false;
            this.isUserFormVisible = false;
            this.payment.paymentState = '';
            this.setSystemMessage([], true);
        },
        retrieveJobDetails() {
            const self = this;
            const data = {
                ...this.objectDetails
            };
            // Let's reset in case user changes job number
            self.isUserDetailRetrieved = false;
            getJobDetailsRequest(data).then((response) => {
                self.isJobDetailRetrieved = true;
                self.buildUsers = cloneObject(response.body.users);
                self.jobDetails = cloneObject(response.body.jobDetails);

                if (response.body.paymentState) {
                    self.payment.paymentState = response.body.paymentState;
                } else {
                    self.setStateBasedOnJobNumber();
                }

                if (self.buildUsers.length > 0) {
                    self.isUserDetailRetrieved = true;
                }
            }).catch((response) => {
                self.setSystemMessage([response.error], false)
            });
        },
        retrieveUserDetails(email, isPrimary) {
            const self = this;
            const data = {
                email: email
            };

            getUserDetailsByEmailRequest(data).then((response) => {
                copyData(response.body.user, this.payer.user);
                copyData(response.body.address, this.payer.address);
                self.payer.isPrimary = (isPrimary === 1);
                self.isUserFormVisible = true;
                self.isUserDetailRetrieved = true;
                self.userDetailsFormKey += 1;
            });
        },
        clearUserDetails() {
            this.payer.user = {
                title: '',
                firstName: '',
                surname: '',
                mobileNumber: '',
                email: '',
            };

            this.payer.address = {
                address1: '',
                address2: '',
                suburb: '',
                state: '',
                postcode: '',
            };

            this.payer.isPrimary = false;

            this.isUserDetailRetrieved = false;
            this.isUserFormVisible = true;
        },
        setStateBasedOnJobNumber() {
            const initial = this.payment.jobNumber.charAt(0);
            let stateID = Object.keys(this.paymentStates).find(key => this.paymentStates[key].charAt(0) === initial);
            if (!stateID) {
                stateID = Object.keys(this.paymentStates).find(key => this.paymentStates[key] === 'VIC');
            }
            this.payment.paymentState = stateID;
        },
        getPaymentFormDropdownItems() {
            const self = this;
            getPaymentFormDropdownItemsRequest({
                billerCodeSelector: self.billerCodeSelector
            }).then((response) => {
                self.paymentTypes = response.body.paymentTypes;
                self.paymentStates = response.body.paymentStates;
                self.paymentMethods = response.body.paymentMethods;
                self.titles = response.body.titles;
                self.surchargeForStates = response.body.surchargeForStates;
                self.receiptTypes = response.body.receiptTypes;
                // When there's only one in the dropdown, and the value is not set yet, set the lone dropdown value as default
                if (!self.payment.receiptTypeID && self.hasOnlyOneReceiptTypeOption) {
                    self.payment.receiptTypeID = Object.keys(self.receiptTypes)[0];
                }
            });
        },
        goToIndex() {
            router.push({
                name: 'Dashboard',
            });
        },
        onChequePaymentFormChange(data) {
            this.cheque = data.cheque;
            this.isChequePaymentFormValid = data.isValid;
        },
        onCreditCardPaymentFormChange(data) {
            this.cc = data.cc;
            this.isCreditCardPaymentFormValid = data.isValid;
        },
        onUserFormChange(data) {
            this.payer = data.userDetails;
            this.isUserFormValid = data.isValid;
        },
        showDeleteModal() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you wish to delete this payment?').then(function()  {
                self.deletePayment();
            });
        },
        deletePayment() {
            const data = { paymentID: this.id };

            deletePaymentRequest(data).then((response) => {
                if (response.status === 200 && response.body.deleted) {
                    this.goToIndex();
                }
                else {
                    this.setSystemMessage(["Failed to delete payment."], false);
                }
            });

        },
        computeAmountAndSurcharge() {
            const self = this;
            if (this.payment.paymentMethod === PaymentMethods.CREDIT_CARD && this.payment.amount) {
                const data = {
                    paymentState: this.payment.paymentState,
                    billerCodeSelector: this.billerCodeSelector,
                    amount: this.payment.amount
                };
                computeAmountAndSurchargeRequest(data).then((response) => {
                    self.amountWithBpointSurcharge = response.body.totalAmount;
                    self.surchargeAmount = response.body.surchargeAmount;
                    self.amountWithoutSurcharge = response.body.amountWithoutSurcharge;
                    self.surchargeForStates = response.body.surchargeForStates;
                });
            } else {
                self.amountWithoutSurcharge = self.payment.amount;
                self.amountWithBpointSurcharge = null;
                self.surchargeAmount = 0;
            }
        },
        resetPaymentMethod(force = false) {
            if ((this.isPreliminaryPaymentType && this.isCreditCardPayment) ||
                (!this.isPreliminaryPaymentType && this.isEftPayment) ||
                (this.isMasterpiecePayment && !this.isCreditCardPayment)) {
                this.payment.paymentMethod = "";
            }

            if (this.isTenderFeePaymentType) {
                // Default amount to $2000 if not previously set
                const amount = this.payment.amount > 0.00 ? this.payment.amount : DEFAULT_TENDER_FEE_AMOUNT;
                this.$set(this.payment, 'amount', amount);
                this.$v.payment.amount.$touch();
            }
        },
        onJobNumberEnter() {
            if (!this.$v.payment.jobNumber.$invalid) {
                this.retrieveJobDetails();
            }
        },
        clearSystemMessage() {
            this.message = [];
        },
        goToPreviousStep() {
            this.step = this.step - 1;
            this.clearSystemMessage();
        },
        goToNextStep() {
            this.step = this.step + 1;
            this.clearSystemMessage();
        },
        setPaymentMethod(method) {
            this.$set(this.payment, 'paymentMethod', method);
            this.$v.payment.paymentMethod.$touch();
            this.computeAmountAndSurcharge();
        },
        getPaymentMethodLabel(label, value) {
            if (value === 'cash' || value === 'cheque') {
                return label;
            } else if (value === 'credit-card') {
                return 'Card';
            } else if (value === 'eft') {
                return 'Transfer';
            }
        },
        getUserName(user) {
            return (user.title ? user.title + " " : "") + user.fullName;
        },
        getUserAddress(address) {
            return address
                ? [address.address1, address.address2, address.suburb, address.state, address.postcode]
                    .filter(item => (item !== "" && item !== null))
                    .map(item => item.trim().replace(/,*$/, ""))
                    .join(", ")
                : "-";
        }
    },
}
</script>

<style>
    .steps {
        align-content: center;
        align-items: center;
        display: flex;
        justify-content: space-around;
        padding: 0px;
        wrap-option: nowrap;
    }

    li.step {
        background-color: white;
        border: 1px solid #A9A9A9;
        border-radius: 1.5em;
        display: inline-block;
        height: 3em;
        line-height: 3em;
        position: relative;
        text-align: center;
        width: 3em;
    }

    li.step::before{
        background: #A9A9A9;
        content: '';
        height: .05em;
        left: -10em;
        position: absolute;
        top: 1.5em;
        width: 10em;
        z-index: -1;
    }

    @media screen and (max-width: 640px) {
        li.step::before{
            left: -4.5em;
            width: 5em;
        }
    }

    li.step:first-child::before {
        display: none;
    }

    li.step > div.active {
        background: #A9A9A9;
        border-radius: 50%;
        color: white;
    }

    .payment-method {
        border: 1px solid lightgray;
        border-radius: 5px;
        cursor: pointer;
        height: 50px;
        padding: 20px;
        width: 50px;
    }

    .payment-method.active {
        background: #1e87f0;
        color: white;

    }

    .customer-link {
        color: #7f8897 !important;
    }
    .customer-link.active {
        font-weight: bold;
    }

    .add-customer-link.active {
        font-weight: bold;
    }
    .add-customer-icon {
        border: 1px solid #1e87f0;
        border-radius: 5px;
        font-weight: bold;
    }
    .add-customer-icon.active {
        background-color: #1e87f0;
        color: white;
    }

    .user-icon {
        background: lightgray;
        border-radius: 50%;
        padding: -.5em;
    }
</style>